<script setup lang="ts">
// Campo de partículas decorativas: pontos que sobem devagar com leve deriva
// lateral e um brilho branco no meio delas. Nasceu no sheet da roletinha e virou
// componente quando o check-in diário pediu o mesmo fundo — em dourado.
//
// Regras da casa que este componente respeita, e que são a razão de ele existir
// num lugar só:
//
// - Só `transform` e `opacity`, as duas propriedades que o compositor anima sem
//   passar pelo layout. 18 pontos animando em cima de `top`/`left` custariam
//   reflow a cada frame.
// - Animação por `@keyframes`, NUNCA `transition`: o `main.scss` mata
//   `transition` no app inteiro, então transição aqui simplesmente não roda.
// - Ruído DETERMINÍSTICO em vez de `Math.random()`. A composição não pode mudar
//   a cada render — no SSR isso seria divergência de hidratação, e no cliente
//   faria o fundo "pular" a cada reabertura do modal.
//
// Onde encaixar: o pai precisa ser `position: relative` e clipar (`overflow:
// clip`), porque as partículas sobem até sair pelo topo. A camada NÃO define o
// próprio `z-index` de propósito — quem hospeda decide, com uma classe própria,
// se ela fica atrás do conteúdo em fluxo (`z-index: -1` + `isolation: isolate`
// no pai) ou apenas no rés do chão de uma pilha já posicionada (`z-index: 0`).
const props = withDefaults(
  defineProps<{
    /** Cor dos pontos e do halo. Aceita qualquer cor CSS. */
    color?: string;
    /** Quantos pontos. 18 é o ponto de equilíbrio entre densidade e custo. */
    count?: number;
    /**
     * Distância que cada ponto sobe antes de reiniciar. Tem que passar da altura
     * do container, senão o ciclo reinicia à vista do jogador — os pontos somem
     * num teto invisível no meio da tela. Container de altura fixa aceita px; se
     * a altura vem do aparelho, `100vh` resolve pra qualquer tamanho.
     */
    travel?: string;
    /**
     * Volume do campo, de 0 a 1. Multiplica o pico de opacidade de cada ponto E
     * encolhe o halo na mesma proporção — os dois juntos, porque é o halo (8px
     * de raio em cima de um ponto de 2–6px) que espalha a cor e faz o campo
     * pesar; baixar só a opacidade deixa uma névoa colorida do mesmo tamanho.
     *
     * `1` é a calibragem original (roletinha e check-in, onde o campo tem a tela
     * quase toda pra si). Fundo de conteúdo denso pede menos. `0` desliga a
     * camada sem o consumidor precisar de `v-if`.
     */
    intensity?: number;
  }>(),
  {
    color: '#5AA9FF',
    count: 18,
    travel: '700px',
    intensity: 1,
  },
);

// Nome diferente da prop de propósito: uma binding de setup com o MESMO nome
// sombreia a prop no template, e o clamp deixaria de ser óbvio na leitura.
const glow = computed(() => Math.min(Math.max(props.intensity, 0), 1));

// Hash de inteiro pra [0,1): a mesma técnica de ruído barato dos shaders. Duas
// chamadas com o mesmo par (índice, sal) devolvem sempre o mesmo número, que é o
// que garante a composição estável.
function particleNoise(index: number, salt: number): number {
  const v = Math.sin((index + 1) * 12.9898 + salt * 78.233) * 43758.5453;

  return v - Math.floor(v);
}

const particles = computed(() =>
  Array.from({ length: props.count }, (_, i) => ({
    left: `${(particleNoise(i, 1) * 100).toFixed(2)}%`,
    size: `${(2 + particleNoise(i, 2) * 4).toFixed(1)}px`,
    duration: `${(10 + particleNoise(i, 3) * 10).toFixed(1)}s`,
    // Delay NEGATIVO: ao abrir o modal as partículas já estão espalhadas pela
    // altura do container, em vez de nascerem todas juntas na base.
    delay: `${(-particleNoise(i, 4) * 20).toFixed(1)}s`,
    drift: `${(particleNoise(i, 5) * 56 - 28).toFixed(0)}px`,
    // Pico de opacidade alto de propósito (0.35 a 0.9): abaixo disso a cor não
    // lê como cor — o ponto vira um cinza claro e o campo inteiro parece branco,
    // que era o efeito antes de medir isto num harness.
    peak: ((0.35 + particleNoise(i, 6) * 0.55) * glow.value).toFixed(3),
    // Uma minoria em branco, só pra pontuar brilho. O corte é alto (≈14%) pelo
    // mesmo motivo: branco é a cor mais brilhante do campo e, em maior
    // proporção, sequestra a leitura — com 35% de brancas o campo dourado do
    // check-in e o azul da roletinha ficavam os dois parecendo prateados.
    light: particleNoise(i, 7) > 0.86,
  })),
);
</script>

<template>
  <!-- Decoração pura: fora da árvore de acessibilidade e sem interceptar
       ponteiro, senão os pontos roubariam o clique do conteúdo. -->
  <div
    aria-hidden="true"
    class="bpf"
    :style="{
      '--bpf-color': color,
      '--bpf-travel': travel,
      '--bpf-glow': glow,
    }"
  >
    <span
      v-for="(particle, i) in particles"
      :key="i"
      class="bpf__dot"
      :class="{ 'bpf__dot--light': particle.light }"
      :style="{
        '--p-left': particle.left,
        '--p-size': particle.size,
        '--p-dur': particle.duration,
        '--p-delay': particle.delay,
        '--p-drift': particle.drift,
        '--p-peak': particle.peak,
      }"
    />
  </div>
</template>

<style lang="scss" scoped>
.bpf {
  @apply absolute inset-0 pointer-events-none select-none;
  // Sem z-index: quem hospeda decide a camada (ver o comentário no script).
}

.bpf__dot {
  @apply absolute rounded-full;

  left: var(--p-left);
  // Nasce logo abaixo da base do container e sobe até sair pelo topo — o clip do
  // pai corta o que passa das bordas.
  top: 100%;
  width: var(--p-size);
  height: var(--p-size);
  background: var(--bpf-color);
  // Halo generoso: é ele que espalha a cor além do ponto de 2–6px e faz o campo
  // ter cor, em vez de ter pontinhos coloridos num fundo neutro. O raio segue o
  // `intensity` — ver o comentário da prop.
  box-shadow: 0 0 calc(8px * var(--bpf-glow, 1)) calc(2px * var(--bpf-glow, 1))
    var(--bpf-color);
  // Estado base é invisível: fora da animação (reduced-motion) nada aparece.
  opacity: 0;
  animation: bpf-rise var(--p-dur) linear var(--p-delay) infinite;
}

.bpf__dot--light {
  background: #fff;
  box-shadow: 0 0 calc(6px * var(--bpf-glow, 1)) calc(1px * var(--bpf-glow, 1))
    rgba(255, 255, 255, 0.7);
}

@keyframes bpf-rise {
  0% {
    transform: translate3d(0, 0, 0) scale(0.85);
    opacity: 0;
  }
  15% {
    opacity: var(--p-peak);
  }
  80% {
    opacity: var(--p-peak);
  }
  100% {
    transform: translate3d(var(--p-drift), calc(-1 * var(--bpf-travel)), 0)
      scale(1.1);
    opacity: 0;
  }
}

// Quem pediu menos animação não recebe movimento decorativo nenhum: sem a
// animação, o `opacity: 0` do estado base já deixa a camada invisível.
@media (prefers-reduced-motion: reduce) {
  .bpf__dot {
    animation: none;
  }
}
</style>
